import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { ChevronDown, ChevronUp, LineChart } from 'lucide-react'
import type { OrderSideValue, TradeIntent } from '@apna/shared'
import {
  INTENT_LIMITS,
  TIER1_INDICES,
  contractLabel,
} from '@apna/shared'
import {
  useFeedDiagnostics,
  useTickSocketState,
} from '@/feed/useTick'
import { onIntent, prefetchChain } from '@/feed/prefetchChain'
import { useWarmChains } from './terminal/useWarmChains'
import { LOAD_MORE_STRIKES, useChainLadder } from './terminal/useChainLadder'
import { expiryLabel } from '@/lib/fmt'
import {
  CHAIN_MODE_HINT,
  DEFAULT_CHAIN_MODE,
  chainModeClass,
  type ChainMode,
} from './terminal/chainMode'
import { BookDrawer, type DrawerTab } from './terminal/BookDrawer'
import {
  DRAWER_DEFAULT_PCT,
  DRAWER_MAX_PCT,
  DrawerResizer,
  isChainHidden,
  useDrawerWidth,
} from './terminal/DrawerResizer'
import { ChainRow } from './terminal/ChainRow'
import { ChainHeader } from './terminal/ChainHeader'
import { GREEKS_CADENCE_MS } from './terminal/useChainGreeks'
import { IndexQuote } from './terminal/IndexStrip'
import { StrategyPicker } from './terminal/StrategyPicker'
import { StrategyBar } from './terminal/StrategyBar'
import { placeOrder } from './terminal/placeOrder'
import { useChainLimitTicket } from './terminal/useChainLimitTicket'
import { useOrderFailureToasts } from './terminal/useOrderFailureToasts'
import { useLeaveGuard } from '@/hooks/useLeaveGuard'
import { useOrders } from '@/store/useOrders'
import { LimitOrderTicket } from './terminal/LimitOrderTicket'
import BasketBuilder from './BasketBuilder'
import type { BasketSeed } from './BasketBuilder/basketSeed'
import { marginForLegs } from './terminal/marginFor'
import {
  BUDGET_PRESETS,
  clickLots,
  type ClickLots,
  type QuantityMode,
} from './terminal/quantity'
import { LotsSize, RupeeSize } from './terminal/SizeControl'
import { notifyFail } from '@/components/Toast'
import type { StrategyDto } from '@/types/strategy'
import { ExpiryStrip } from './terminal/ExpiryStrip'
import { ChainAddons } from './terminal/ChainAddons'
import { ChainModePicker } from './terminal/ChainModePicker'
import {
  addonsFor,
  shedColumns,
  chainColumnsClass,
  CHAIN_ADDONS_KEY,
  parseChainAddons,
  type ChainAddon,
} from './terminal/chainAddons'
import { InstrumentPicker } from './terminal/InstrumentPicker'
import { feedStateOf } from './terminal/feedState'
import { useChainKeys } from './terminal/useChainKeys'
import {
  admitClick,
  clickKey,
  freshThrottle,
  type ThrottleState,
} from './terminal/clickThrottle'
import { provisionalStore } from './terminal/provisionalStore'
import { accountStore } from '@/store/account'
import { TerminalNotice } from './terminal/TerminalNotice'
import { orderStore } from '@/store/orders'
import { tickStore } from '@/feed/tickStore'
import { tickSocket } from '@/feed/tickSocket'

/**
 * Terminal — Phase 1.
 *
 * This is the catalogue, not the trading screen. It renders the real option
 * chain structure out of Postgres with NO prices, because there is no feed
 * until Phase 3 and a chain showing invented numbers is worse than a chain
 * showing none. The price columns are present but empty, marked `cell-stale`,
 * so the layout is the one Phase 3 fills rather than one it has to replace.
 *
 * Phase 1 exit criterion lives here: type an underlying, get real instruments
 * back from the database.
 */

/**
 * The row that widens the ladder.
 *
 * Revealed strikes DO get prices now: widening subscribes them, the gateway
 * forwards that as demand, and the engine acquires them as Tier 2 (3.16).
 * They arrive a moment later than the core window, because that is a round
 * trip to the broker rather than a slice of something already streaming.
 *
 * They can also be refused — `POOL_EXHAUSTED`, or the per-user quota — in
 * which case they stay dashed, which is the honest rendering.
 */
export default function Terminal() {
  const [underlying, setUnderlying] = useState<string>(TIER1_INDICES[0])
  useWarmChains()
  const [pickerOpen, setPickerOpen] = useState(false)
  /**
   * The strategy the header's dropdown has armed, and the direction a fire
   * button asked for. Both are handed to `StrategyPicker` rather than placing
   * anything directly — the resolved legs and the typed-LIVE confirmation are
   * the reason it exists, and a header button must not route around them.
   */
  const [armedStrategy, setArmedStrategy] = useState<StrategyDto | null>(null)
  /**
   * Which instrument the top row's quote is showing.
   *
   * Null means "whatever the chain is on", which is the answer every time
   * except when India VIX has been clicked — it has no option contracts, so
   * it can be read but not traded, and selecting it must leave the ladder
   * alone. Any change to the underlying drops the pin, because a quote naming
   * one instrument above a ladder for another is the mismatch that gets
   * traded on.
   */
  const [pickerIntent, setPickerIntent] = useState<TradeIntent>('SELL')

  /**
   * The `ATM_OFFSET` steps the armed strategy needs, lowest first.
   *
   * A JOINED STRING in the dependency list below, because this is a fresh
   * array on every render and an effect keyed on the array itself would run
   * on every one of them.
   */
  const strategySteps = useMemo(
    () =>
      (armedStrategy?.legs ?? [])
        .map((l) =>
          l.strikeCriteria?.kind === 'ATM_OFFSET' ? l.strikeCriteria.steps : 0,
        )
        .filter((n) => Number.isFinite(n)),
    [armedStrategy],
  )

  /*
   * ── THE LADDER — ROADMAP 9.66 ──────────────────────────────────────────
   *
   * Everything about "what IS the chain for this underlying" lives in
   * `useChainLadder`: the catalogue, the anchored window, the greeks pass,
   * the subscriptions and the lot size. It was extracted when the basket
   * builder became the second screen that draws a ladder — not to shorten
   * this file, but because two copies of an anchored window are two chains
   * that can disagree about where the money is.
   *
   * What stays HERE is everything that depends on what this screen DOES
   * with the ladder: which columns are drawn, whether the open-interest bar
   * is shown, and what a click on B or S sends.
   */
  const {
    rows,
    visible,
    hiddenAbove,
    hiddenBelow,
    showMoreAbove,
    showMoreBelow,
    expiries,
    expiry,
    setExpiry,
    loading,
    error,
    spot,
    step,
    atm,
    greeks,
    lotSize,
    isExpiryDay,
    chainKinds,
    previewRows,
    bodyRef,
  } = useChainLadder(underlying, { strategySteps })


  /*
   * ── trading (ROADMAP 4.10-4.13) ────────────────────────────────────────
   *
   * `placeOrder` is the only thing that sends, and the selected connection is
   * the only place that knows which account. Everything above it is the
   * catalogue and the feed.
   */
  /*
   * READ from the store, not owned here. This screen used to fetch the
   * connections and push them in, which made it the authoritative owner of
   * state the whole shell depends on — so a cold load of /portfolio found an
   * empty store and read as flat until the Terminal had been visited.
   */
  useSyncExternalStore(
    accountStore.subscribe,
    accountStore.getSnapshot,
    accountStore.getSnapshot,
  )
  const { connections, selectedId } = accountStore.getSnapshot()
  /** True while an order is in flight, so a double click cannot stack two. */
  const [firing, setFiring] = useState(false)

  /*
   * ── HOW FAST THE CHAIN MAY BE FIRED — ROADMAP 9.15 ─────────────────────
   *
   * A REF, NOT STATE, and that is the whole reason this is written out
   * rather than being a `useState`. Two clicks 40 ms apart are one React
   * batch: state read inside the second handler would still be the value
   * from before the first, so a state-based limiter would admit both and
   * refuse nothing. A ref is written synchronously and the next handler
   * sees it.
   *
   * It also must not re-render. The limiter's job is to make the terminal
   * feel calmer under a burst; a re-render per click would do the opposite.
   *
   * `noticeAt` coalesces the refusals. Eight blocked clicks is ONE sentence
   * — stacking eight identical toasts is the same noise the limiter exists
   * to remove, wearing a different hat.
   */
  const throttle = useRef<ThrottleState | null>(null)
  const noticeAt = useRef(0)
  const admit = useCallback(
    (tokens: readonly number[], side: string): boolean => {
      const now = Date.now()
      const verdict = admitClick(
        throttle.current ?? freshThrottle(now),
        clickKey(tokens, side),
        now,
      )
      throttle.current = verdict.state
      if (verdict.allow) return true
      if (now - noticeAt.current > 1_500) {
        noticeAt.current = now
        // The headline is the only thing a trader reads mid-burst, so it is
        // the answer to the question they actually have: did it go?
        notifyFail('Not sent', verdict.why)
      }
      return false
    },
    [],
  )
  const [payoffOpen, setPayoffOpen] = useState(false)
  /**
   * The basket builder — ROADMAP 9.66.
   *
   * A separate surface rather than a panel on this screen, and deliberately:
   * on THIS ladder B and S place an order on the click, with no confirmation
   * (4.15), and in the builder they add a leg. One button with two meanings
   * decided by whether a panel is open is the single most dangerous control
   * this product could ship, so the two live on two screens.
   */
  const [basketOpen, setBasketOpen] = useState(false)
  /**
   * Legs handed over by the strategy ticket's Adjustments button — 9.69.
   *
   * Null when the builder was opened from the strategy list, which is the
   * "build one from scratch" path and starts by asking for an underlying.
   */
  const [basketSeed, setBasketSeed] = useState<BasketSeed | null>(null)

  /*
   * The chain runs FULL WIDTH by default and the panel visits.
   *
   * A docked panel costs the chain 340px all day for something looked at a
   * few times an hour.
   */
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('POSITIONS')
  const [positionsRefresh, setPositionsRefresh] = useState(0)
  /*
   * Which columns the ladder draws — 4.20.
   *
   * NOT REMEMBERED. It used to persist, and a trader who had glanced at
   * the four risks once opened the terminal in Greeks the next morning — a
   * ladder with no open interest on it — and had to work out what had
   * happened before they could work out the market. See `chainMode.ts`.
   */
  const [chainMode, setChainMode] = useState<ChainMode>(DEFAULT_CHAIN_MODE)
  const pickChainMode = useCallback((next: ChainMode) => setChainMode(next), [])
  /*
   * WHICH SHED COLUMNS THE TRADER ASKED BACK — 9.25.
   *
   * Remembered like the mode, and for the same reason: it is a statement
   * about how this person reads a ladder, not about this session. It is
   * only CONSULTED on a split chain — `chainColumns` owns that rule — so
   * ticking IV back on does not change anything at full width, where it was
   * never off.
   */
  const [addons, setAddons] = useState<ChainAddon[]>(() => {
    try {
      return parseChainAddons(JSON.parse(localStorage.getItem(CHAIN_ADDONS_KEY) ?? '[]'))
    } catch {
      // Private windows, blocked site data, and anything an older build
      // left behind that is not JSON. The default is the documented one.
      return []
    }
  })
  const pickAddons = useCallback((next: ChainAddon[]) => {
    setAddons(next)
    try {
      localStorage.setItem(CHAIN_ADDONS_KEY, JSON.stringify(next))
    } catch {
      // Not remembering it is survivable; failing to toggle is not.
    }
  }, [])
  // The chain/book split, remembered across sessions.
  const { pct: drawerPct, setPct: setDrawerPct } = useDrawerWidth()

  /**
   * The book has the whole workspace and the chain is hidden.
   *
   * Guarded on `drawerOpen` as well as the split, because the two are
   * independent: collapsing the drawer leaves the remembered 100% behind,
   * and a chain hidden by a drawer that is no longer there is a blank
   * screen with no way out of it.
   */
  const chainFull = drawerOpen && isChainHidden(drawerPct)

  /*
   * ── WHERE OPEN INTEREST IS DRAWN ────────────────────────────────────────
   *
   * Decided here rather than inside the row, because it depends on something
   * a row cannot see — how much of the window the chain has.
   *
   *   MARKET    always. OI is half of what that view is FOR.
   *   COMBINED  only with the whole window. Split, that view is deliberately
   *             the strike and the premium and nothing else, and a bar under
   *             every price is the "nothing else" it exists to avoid.
   *   GREEKS    never. It has no OI today and the four risks are the point.
   */
  const showOi = chainMode === 'MARKET' || (chainMode === 'COMBINED' && !drawerOpen)

  /*
   * ── WHICH OPTIONAL COLUMNS THE LADDER DRAWS ─────────────────────
   *
   * Decided here for the reason `showOi` is: it depends on how much of the
   * window the chain has, which neither a row nor the stylesheet can see.
   * `drawerOpen` rather than a measured width, because the question is what
   * the TRADER is doing — managing positions — and not how many pixels are
   * left; the pixel question is already answered by the container queries.
   */
  const shed = shedColumns({ mode: chainMode, split: drawerOpen, addons })
  const offeredAddons = addonsFor(chainMode)

  const showChain = useCallback(
    () => setDrawerPct(DRAWER_DEFAULT_PCT),
    [setDrawerPct],
  )
  const showBookFullScreen = useCallback(() => {
    setDrawerOpen(true)
    setDrawerPct(DRAWER_MAX_PCT)
  }, [setDrawerPct])

  /*
   * Escape leaves full screen. The worded button is the discoverable way
   * back; this is the one a trader reaches for without looking, and it
   * costs nothing to honour. Bound only while full screen so it cannot
   * steal Escape from a dialog.
   */
  useEffect(() => {
    if (!chainFull || payoffOpen || pickerOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') showChain()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chainFull, payoffOpen, pickerOpen, showChain])

  /*
   * A once-a-second clock, for the IST readout and the market pill.
   *
   * One interval for the screen rather than one per consumer, and it only
   * drives those two: nothing priced re-renders from it.
   */
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(t)
  }, [])

  /*
   * ── SIZE: LOTS, OR A RUPEE BUDGET ──────────────────────────────────────
   *
   * Two ways a desk says the same thing. Lots is the plain one; ₹ divides a
   * budget by the premium of the contract being clicked, so the same amount
   * buys more of a cheap strike than a dear one. `autoLotsFor` does the
   * arithmetic and rounds DOWN — a budget is a ceiling, not a target.
   *
   * ARMED IS A SEPARATE STEP, and it matters more now than it did in the
   * mockup it comes from: a click fires immediately (4.15), so a half-typed
   * ₹5,00,000 that is momentarily ₹5 must not be able to size an order.
   * Changing the amount disarms.
   *
   * ₹ SIZES SINGLE LEGS ONLY. A strategy basket fires at the lot count,
   * because sizing a basket by premium is only meaningful when it is a
   * DEBIT: for a credit structure the premium is received rather than
   * spent, and dividing a budget by it produces a number with no relation
   * to the capital at risk. [B9] leaves us no margin figure to use instead.
   */
  /*
   * The ATM call's premium, which is what the ₹ hint prices a budget
   * against. Read from the store on the one-second clock rather than
   * subscribed: a hint that re-rendered the header at feed rate would be
   * §4.7's mistake for the sake of an approximation.
   */
  const atmPremium = useMemo(() => {
    if (atm === null) return 0
    const row = visible.find((r) => r.strike === atm)
    const token = row?.ce?.brokerToken ? Number(row.ce.brokerToken) : 0
    return token > 0 ? (tickStore.snapshot(token)?.ltp ?? 0) : 0
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now` is the clock
  }, [atm, visible, now])

  const [lots, setLots] = useState(1)
  const [sizeMode, setSizeMode] = useState<QuantityMode>('LOTS')
  const [budget, setBudget] = useState<number>(BUDGET_PRESETS[1].value)
  const [armed, setArmed] = useState(false)

  /*
   * Recently opened underlyings, most recent first.
   *
   * In memory only: it is a convenience within a session, and persisting it
   * would mean deciding what to do when one is delisted.
   */
  const [recent, setRecent] = useState<string[]>([])
  const pickUnderlying = useCallback((key: string) => {
    setUnderlying(key)
    setRecent((prev) => [key, ...prev.filter((x) => x !== key)].slice(0, 6))
  }, [])
  const [highlight, setHighlight] = useState<{
    token: number
    strike: number
    optionSide: 'CE' | 'PE'
  } | null>(null)

  useEffect(() => {
    const off = tickSocket.onExecution((event) => {
      orderStore.applyEvent(event)
    })
    return off
  }, [])

  const selected = useMemo(
    () => connections.find((c) => c.id === selectedId) ?? null,
    [connections, selectedId],
  )

  /*
   * Switching accounts re-reads the book. The selection itself is written and
   * persisted by the store; what belongs here is the consequence for THIS
   * screen, which is that the positions on display are another account's.
   */
  useEffect(() => {
    setPositionsRefresh((n) => n + 1)
  }, [selectedId])

  /*
   * The ATM call's premium, which is what rupee mode sizes against.
   *
   * Read from the store rather than held in state: it ticks, and putting a
   * price into React state here would re-render the whole screen at feed
   * rate — the thing §4.7 is about.
   */

  const sendLots = Math.max(0, Math.floor(lots))

  /*
   * Stable across renders, so `ChainRow`'s `memo` is worth having — there
   * are up to 470 rows and a new closure each render would walk every one.
   */
  const openDrawer = useCallback((tab: DrawerTab) => {
    setDrawerTab(tab)
    setDrawerOpen(true)
    if (tab === 'POSITIONS') setPositionsRefresh((n) => n + 1)
  }, [])

  /**
   * Show the positions pane NOW, for a row that has just been painted.
   *
   * ───────────────────────────────────────────────────────────────────────
   * WHY THIS IS NOT `openDrawer('POSITIONS')`
   *
   * Two differences, both of which matter on a burst of clicks.
   *
   * It runs on the CLICK rather than on the answer. 9.26 paints the row
   * from the click, but the row was being painted into a drawer that only
   * opened once `placeOrder` resolved — so the first click of a burst
   * still showed nothing until a round trip came back, which is the delay
   * that kept being reported as "the row takes time to appear". The row
   * was already there; the container was not.
   *
   * And it does NOT bump `positionsRefresh`. The provisional row is
   * already in the store and the server's push reconciles it, so a refetch
   * here buys nothing — while four or five of them, one per click, is the
   * burst of redundant reads that made a fast ladder feel glitchy.
   * ───────────────────────────────────────────────────────────────────────
   */
  const revealPositions = useCallback(() => {
    setDrawerTab('POSITIONS')
    setDrawerOpen(true)
  }, [])

  /** How many lots one click on THIS contract sends — see `clickLots`. */
  /**
   * How many lots this click sends — for one contract or for a whole basket.
   *
   * ───────────────────────────────────────────────────────────────────────
   * ONE LOT COUNT FOR THE WHOLE BASKET, WHICH IS WHAT MAKES IT A BASKET
   *
   * In ₹ mode a per-leg answer would size the cheap side larger than the
   * dear one — a 24,150 straddle on a ₹50,000 budget comes out 13 calls
   * against 8 puts, which is a directional ratio spread wearing a
   * straddle's name. The combined premium is divided once and the answer
   * applies to every leg, so the shape placed is the shape shown.
   *
   * A SOLD basket is sized on the margin it BLOCKS, not the premium it
   * collects, and `marginForLegs` already prices the legs TOGETHER — so a
   * short straddle gets the real netted requirement rather than twice a
   * single leg's.
   * ───────────────────────────────────────────────────────────────────────
   */
  const lotsForClick = useCallback(
    (
      legs: readonly {
        token: number
        strike: number
        optionType: 'CE' | 'PE'
      }[],
      side: OrderSideValue,
    ): ClickLots => {
      const ltp = (token: number): number => tickStore.snapshot(token)?.ltp ?? 0
      const perLot =
        side === 'SELL'
          ? marginForLegs(
              legs.map((l) => ({
                token: l.token,
                side,
                optionType: l.optionType,
                strike: l.strike,
                lots: 1,
                price: ltp(l.token),
              })),
              { greeks, spot, lotSize: lotSize ?? 0, isExpiryDay },
            )
          : null

      return clickLots({
        mode: sizeMode,
        lots: sendLots,
        armed,
        budget,
        side,
        // The CLICKED contracts' own prices, summed. The same budget buys
        // more of a cheap strike than a dear one, which is the point of
        // sizing by money; for a basket, what it buys is one of EACH leg.
        premiumPerUnit: legs.reduce((sum, l) => sum + ltp(l.token), 0),
        marginPerLot: perLot?.ok ? perLot.margin.total : null,
        lotSize: lotSize ?? 0,
        maxLots: INTENT_LIMITS.maxLots,
      })
    },
    [sizeMode, sendLots, armed, budget, lotSize, greeks, spot, isExpiryDay],
  )

  /*
   * Token -> tick size, built ONCE per ladder.
   *
   * This was a linear scan that `flatMap`ped the whole ladder on every call
   * — up to 470 rows into a 940-element array — from inside an inline arrow
   * that also made `limitTicket.open` a new function on every render. See
   * `useChainLimitTicket` for what that cost the chain's `memo`.
   */
  const tickSizes = useMemo(() => {
    const map = new Map<number, number>()
    for (const row of rows) {
      for (const item of [row.ce, row.pe]) {
        const token = Number(item?.brokerToken)
        if (item && Number.isFinite(token) && token > 0)
          map.set(token, item.tickSize ?? 0.05)
      }
    }
    return map
  }, [rows])

  /*
   * A SHARPER REASON WHILE AN ORDER IS IN THE AIR.
   *
   * The tab is already held for the whole signed-in session — see `Protected`
   * in `App.tsx`, which is where the arming lives and why every screen
   * prompts, not just this one. This adds a second claim that says WHY in the
   * one case where the answer is interesting: an intent that was sent and not
   * yet answered, which this tab is the only witness to.
   *
   * Reasons are never shown to the user — the browser writes its own sentence
   * — so this is for `leaveGuard.held()` and for whoever next asks why a tab
   * refused to close quietly.
   *
   * Still nothing about positions or armed protection. Those are the
   * engine's: `PremiumOrderMonitor` evaluates app protection server-side and
   * exits with no browser open.
   */
  const orders = useOrders()
  const inFlight = orders.filter(
    (o) => o.state === 'SUBMITTING' || o.state === 'AMBIGUOUS',
  ).length
  useLeaveGuard(
    'orders-in-flight',
    inFlight > 0,
    `${inFlight} order${inFlight === 1 ? '' : 's'} still waiting for an answer from the broker`,
  )

  /*
   * The one place a refusal is spoken. Mounted here because Terminal is the
   * only screen that places orders, and because it must be mounted EXACTLY
   * once — two copies would raise two toasts for the same rejected leg.
   */
  useOrderFailureToasts()

  const limitTicket = useChainLimitTicket({
    connection: selected,
    underlying,
    expiry,
    lotSize: lotSize ?? 0,
    // The screen's own sizing, so ₹ mode reaches the ticket intact.
    lotsFor: (selection, side) =>
      lotsForClick(
        selection.legs.map((l) => ({
          token: l.token,
          strike: selection.strike,
          optionType: l.optionType,
        })),
        side,
      ),
    tickSizeOf: (token) => tickSizes.get(token) ?? 0.05,
  })

  /**
   * One leg of a provisional row, from what the click already knows — 9.26.
   *
   * CARRIES NO PRICE, deliberately. The contract, the side and the ordered
   * quantity are facts about what we are sending; an entry price or a P&L
   * would be the broker's to report, and a rejected order would leave a
   * trader having read a number on a position that never existed.
   */
  const provisionalLeg = useCallback(
    (
      token: number,
      side: OrderSideValue,
      lots: number,
      strike: number,
      optionType: 'CE' | 'PE',
    ) => ({
      token,
      side,
      qty: lots * (lotSize ?? 0),
      lotSize: lotSize ?? 0,
      strike,
      optionType,
      expiry: expiry ?? '',
      // The same builder the drawer and the journal use, so one contract is
      // never spelled two ways on one screen.
      symbol: contractLabel({
        underlying,
        strike,
        optionType,
        expiry: expiry === null ? null : new Date(expiry),
        fallback: `${underlying} ${strike} ${optionType}`,
      }),
    }),
    [lotSize, expiry, underlying],
  )

  const fireLeg = useCallback(
    (
      token: number,
      strike: number,
      optionSide: 'CE' | 'PE',
      side: OrderSideValue,
    ) => {
      setHighlight({ token, strike, optionSide })

      /*
       * SENT ON THE CLICK. No ticket, no dialog, no typed word — see
       * `placeOrder.ts` for the decision and what it costs. The toast is the
       * whole of the answer, so it names the contract that went.
       */
      const contract = `${strike} ${optionSide}`
      // Before sizing and before sending: a refused click must not reach the
      // network at all, which is the only version of this that helps.
      if (!admit([token], side)) return
      const sized = lotsForClick(
        [{ token, strike, optionType: optionSide }],
        side,
      )
      if (!sized.ok) {
        notifyFail(`${side} ${contract} not placed`, sized.why)
        return
      }
      const orderLots = sized.lots

      /*
       * ── THE ROW IS PAINTED FROM THE CLICK — ROADMAP 9.26 ──────────────
       *
       * Not from the server's answer. Measured on 2026-11-03, the fill is
       * about two seconds of broker round trips away and two attempts to
       * shorten that (9.22, 9.25) proved it is XTS's latency, not ours.
       * Everything a position row needs is already known here, and it is
       * not a guess — it is what we are about to ask for.
       *
       * The id is minted HERE rather than inside `placeOrder`, because the
       * provisional row has to be keyed on it before the send and the
       * server echoes it back as `forRequest` to retire exactly this row.
       */
      const requestId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`
      if (selected) {
        provisionalStore.place({
          clientRequestId: requestId,
          connectionId: selected.id,
          brokerLabel: selected.label,
          mode: selected.mode,
          underlying,
          at: Date.now(),
          legs: [provisionalLeg(token, side, orderLots, strike, optionSide)],
        })
        revealPositions()
      }

      void (async () => {
        setFiring(true)
        const result = await placeOrder(
          {
            legs: [{ token, side, lots: orderLots }],
            label: `${side} ${orderLots} × ${contract}`,
            clientRequestId: requestId,
          },
          selected,
        )
        setFiring(false)
        /*
         * NO TOAST ON SUCCESS. Accepted is not filled, and the pane
         * `revealPositions` opened on the click already shows the order with
         * its real state — the toast was the less true of two answers to one
         * question, and a ladder worked at speed stacked one per click. A
         * refusal still speaks: `useOrderFailureToasts` watches for the ones
         * that arrive after this handler has returned.
         */
        if (result.ok) return
        // Nothing was sent, so the row it promised must go at once.
        provisionalStore.settle(requestId)
        notifyFail(`${side} ${contract} not placed`, result.reason ?? undefined)
      })()
    },
    [lotsForClick, selected, revealPositions, admit],
  )

  /**
   * Both sides of one strike, in ONE order — ROADMAP 4.18.
   *
   * ───────────────────────────────────────────────────────────────────────
   * ONE INTENT, AND THAT IS THE WHOLE FEATURE
   *
   * Two clicks on B would place two single-leg Trades that happen to be
   * near each other. This sends one `OrderIntentRequest` carrying two legs,
   * which `openTrade` already writes as one Trade with two TradeLegs under
   * one idempotency key — so the Positions drawer draws it as a group, the
   * payoff curve plots it as one shape, and EXIT ALL closes it as one
   * thing.
   *
   * It also removes a hazard the two-click version has: between the two
   * sends, a refusal, a disconnect or a reload leaves one leg naked. An
   * all-or-nothing write cannot.
   *
   * CALL FIRST, deliberately. `TradeLeg.seq` fixes dispatch order (see the
   * schema), and a basket is read top-down everywhere it is drawn — the
   * card, the journal, the payoff legend. Calls before puts is the order a
   * chain already puts them in.
   * ───────────────────────────────────────────────────────────────────────
   */
  const fireCombo = useCallback(
    (
      strike: number,
      ceToken: number,
      peToken: number,
      side: OrderSideValue,
    ) => {
      setHighlight({ token: ceToken, strike, optionSide: 'CE' })

      const legs = [
        { token: ceToken, strike, optionType: 'CE' as const },
        { token: peToken, strike, optionType: 'PE' as const },
      ]
      const what = `${strike.toLocaleString('en-IN')} CE+PE`
      if (!admit([ceToken, peToken], side)) return
      const sized = lotsForClick(legs, side)
      if (!sized.ok) {
        notifyFail(`${side} ${what} not placed`, sized.why)
        return
      }
      const orderLots = sized.lots

      /*
       * PAINTED FROM THE CLICK TOO — both legs, as one row.
       *
       * The single-leg path has done this since 9.26; this one had been
       * left behind, so a combo click still waited on the round trip to
       * show anything. One intent, one provisional row, two legs — the
       * same shape the server will push back under `forRequest`.
       */
      const requestId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`
      if (selected) {
        provisionalStore.place({
          clientRequestId: requestId,
          connectionId: selected.id,
          brokerLabel: selected.label,
          mode: selected.mode,
          underlying,
          at: Date.now(),
          legs: [
            provisionalLeg(ceToken, side, orderLots, strike, 'CE'),
            provisionalLeg(peToken, side, orderLots, strike, 'PE'),
          ],
        })
        revealPositions()
      }

      void (async () => {
        setFiring(true)
        const result = await placeOrder(
          {
            legs: legs.map((l) => ({ token: l.token, side, lots: orderLots })),
            label: `${side} ${orderLots} × ${what}`,
            clientRequestId: requestId,
          },
          selected,
        )
        setFiring(false)
        // Silent on success, as the single-leg path above is, and for the
        // same reason.
        if (result.ok) return
        provisionalStore.settle(requestId)
        notifyFail(`${side} ${what} not placed`, result.reason ?? undefined)
      })()
    },
    [
      lotsForClick,
      selected,
      revealPositions,
      admit,
      underlying,
      provisionalLeg,
    ],
  )

  // F1 / F2 act on the highlighted strike, and send exactly as a click does.
  // Disabled while one is in flight so a held key cannot stack orders.
  const keyTrade = useCallback(
    (side: OrderSideValue) => {
      if (!highlight) return
      fireLeg(highlight.token, highlight.strike, highlight.optionSide, side)
    },
    [highlight, fireLeg],
  )
  useChainKeys({
    onBuy: () => keyTrade('BUY'),
    onSell: () => keyTrade('SELL'),
    enabled: !firing && highlight !== null && !limitTicket.ticket,
  })

  // ── feed state: ticks arriving, market shut, or genuinely offline ──────
  const socketState = useTickSocketState()
  const diagnostics = useFeedDiagnostics()
  const feed = useMemo(
    () =>
      feedStateOf({
        socketState,
        lastFrameAt: diagnostics.socket.lastFrameAt,
        now,
        simulationActive: diagnostics.socket.marketSource === 'SIMULATION',
      }),
    [
      socketState,
      diagnostics.socket.lastFrameAt,
      diagnostics.socket.marketSource,
      now,
    ],
  )
  const clock = useMemo(
    () =>
      new Date(now).toLocaleTimeString('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour12: false,
      }),
    [now],
  )

  /*
   * The OI scale for the bars — the largest on each side of the VISIBLE
   * window, so the tallest bar is always the busiest strike on screen
   * rather than one scrolled out of view.
   *
   * Reads the store directly rather than subscribing: a bar's own cell
   * re-renders on its own tick, and re-deriving the maximum 15 times a
   * second would re-render the whole table to move one bar a pixel.
   */
  const { maxCeOi, maxPeOi, chain } = useMemo(() => {
    let ce = 0
    let pe = 0
    /*
     * The priced count rides along on this walk rather than getting a pass of
     * its own: the notice needs to know whether a live feed is actually
     * pricing anything, and the loop is already here, already on the clock
     * rather than on the feed, and already reading the same slots.
     */
    let shown = 0
    let priced = 0
    for (const r of visible) {
      const ceSnap = r.ce?.brokerToken
        ? tickStore.snapshot(Number(r.ce.brokerToken))
        : null
      const peSnap = r.pe?.brokerToken
        ? tickStore.snapshot(Number(r.pe.brokerToken))
        : null
      const c = ceSnap?.oi ?? 0
      const p = peSnap?.oi ?? 0
      if (c > ce) ce = c
      if (p > pe) pe = p
      if (r.ce?.brokerToken) {
        shown += 1
        if ((ceSnap?.ltp ?? 0) > 0) priced += 1
      }
      if (r.pe?.brokerToken) {
        shown += 1
        if ((peSnap?.ltp ?? 0) > 0) priced += 1
      }
    }
    return { maxCeOi: ce, maxPeOi: pe, chain: { shown, priced } }
  }, [visible, now])

  return (
    <div className="tr flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-hidden p-4">
        {/*
          ── the top row ───────────────────────────────────────────────────
          WHAT AM I LOOKING AT and WHAT AM I DOING ABOUT IT, left to right:
          which instrument, what it costs, which structure, how much, and
          the two buttons that send it. Everything here names the thing on
          screen; nothing here acts on it. The row below is the opposite —
          size, strategy, fire — and splitting them that way is why neither
          needs a second line.

          The instrument search and the expiry moved up out of the chain
          header to make that split, which is also what lets the header below
          hold the fire buttons on one row at half width.
        */}
        <div className="tr-ixbar shrink-0">
          {/*
            ── ONE ROW: WHAT, AT WHAT PRICE, HOW MUCH, AND GO ──────────

            The NIFTY/BANKNIFTY/SENSEX tabs are gone. They were three of
            the underlyings this platform lists, holding permanent width
            because they were the first three built; the search box
            reaches all of them, and reaches those three in two
            keystrokes.

            The expiry dropdown has gone down to its own strip over the
            chain, where the whole series is visible at once instead of
            one date behind a click — see `ExpiryStrip`.

            That freed the width for the controls that actually send an
            order, which now sit beside the instrument they act on rather
            than a row below it. Left to right: WHAT is being traded,
            what it COSTS, WHICH structure, HOW MUCH, and the two buttons
            that do it.
          */}
          <InstrumentPicker
            value={underlying}
            onChange={pickUnderlying}
            recent={recent}
          />

          {/* The quote follows the SELECTED instrument now. It used to
              follow a hover pin the index tabs set, and with the tabs
              gone nothing can pin it to anything else. */}
          <IndexQuote underlying={underlying} />

          <StrategyBar
            strategy={armedStrategy}
            onChange={setArmedStrategy}
            onFire={(intent) => {
              setPickerIntent(intent)
              setPickerOpen(true)
            }}
            rows={previewRows}
            chainKinds={chainKinds}
            spot={spot}
            step={step}
            lotSize={lotSize ?? 0}
            lots={sendLots}
            onBuildBasket={() => {
              setBasketSeed(null)
              setBasketOpen(true)
            }}
            viewControl={
              <div className="mr-1 shrink-0">
                {/*
                  A LIST, not a segmented control. Three views no longer
                  fit the strip beside Buy/Sell Strategy without crowding
                  the buttons that send orders, and a list has room to say
                  what each one is FOR — which three one-word chips cannot.
                */}
                <ChainModePicker
                  value={chainMode}
                  onChange={pickChainMode}
                  title={`${CHAIN_MODE_HINT[chainMode]} · Black–76 · per unit · Θ/day · ν & ρ per 1% · updates every ${GREEKS_CADENCE_MS / 1000}s${
                    /*
                     * "Last computed 17:22" beside a chain of dashes is a
                     * pass that ran and refused, which reads as a pass that
                     * worked. A settled expiry says so instead.
                     */
                    greeks.years <= 0 && greeks.computedAt !== null
                      ? ' · This expiry has settled, so there is no volatility to solve for'
                      : greeks.computedAt
                        ? ` · Last computed ${new Date(greeks.computedAt).toLocaleTimeString('en-IN')}`
                        : ' · Waiting for option prices'
                  }`}
                />
              </div>
            }
            sizeControl={
              <div className="flex items-center gap-2">
                {/*
                QTY or ₹. The toggle swaps the control rather than
                showing both: two live size inputs at once is two
                answers to "how much", and only one of them sends.
              */}
                <div
                  className="tr-sizemode"
                  role="group"
                  aria-label="Size by"
                >
                  {(['LOTS', 'RUPEES'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`size-mode-${m === 'LOTS' ? 'qty' : 'rupees'}`}
                      aria-pressed={sizeMode === m}
                      onClick={() => {
                        setSizeMode(m)
                        // Switching away disarms: an amount armed for one
                        // mode must not be live when you come back to it.
                        setArmed(false)
                      }}
                      className={sizeMode === m ? 'on' : ''}
                    >
                      {m === 'LOTS' ? 'LOTS' : '₹'}
                    </button>
                  ))}
                </div>

                {sizeMode === 'RUPEES' ? (
                  <RupeeSize
                    budget={budget}
                    armed={armed}
                    lotSize={lotSize ?? 0}
                    atmPremium={atmPremium}
                    onBudget={(v) => {
                      setBudget(v)
                      setArmed(false)
                    }}
                    onArm={() => setArmed(true)}
                  />
                ) : (
                  <LotsSize
                    lots={lots}
                    lotSize={lotSize ?? 0}
                    onLots={setLots}
                  />
                )}
              </div>
            }
          />
        </div>

        {/*
          ── NO GAP BETWEEN THE PANES ──────────────────────────────────────
          The 14px gutter is gone and the handle IS the separation: ten
          pixels of hit area around a hairline. Two cards floating apart read
          as two unrelated screens; a line between them reads as one
          workspace split in two, which is what it is.

          The split itself is `--tr-drawer-w` on this element, and REACT DOES
          NOT WRITE IT — `DrawerResizer` sets it on the DOM directly. Rendering
          it here would mean any unrelated re-render mid-drag (the one-second
          clock, a tick, an order event) re-applied the committed value and
          snapped the divider back under the cursor.
        */}
        <div className="tr-ws flex min-h-0 min-w-0 flex-1 items-stretch">
          <section
            className="tr-card tr-chain relative"
            aria-label="Option chain"
          >
            {/*
              FLOATS OVER THE LADDER, bottom right.
              
              Opens the held-trade payoff drawer, independent of the chain
              or account currently selected for execution. A
              solid pill in the accent, which is the only strong colour on
              this screen that is not a buy or a sell: a trader must never
              mistake it for either.
            */}
            <button
              type="button"
              onClick={() => setPayoffOpen(true)}
              data-testid="open-payoff-dock"
              className="tr-payoff-fab"
            >
              <LineChart size={14} />
              <span>Payoff Chart</span>
            </button>

            {/*
              ── ONE NOTICE, NOT TWO ─────────────────────────────────────
              The offline banner and the no-feed-account banner could both
              be on screen at once, and when they were, the second was the
              cause of the first. `terminalNoticeOf` picks whichever one the
              reader should act on; see its header for the ladder.
            */}
            <TerminalNotice
              feed={feed}
              socketState={socketState}
              /*
               * 2.7. Only the account the NEXT order would go to. Another
               * account being degraded is a fact for the Accounts screen;
               * here it would be a red banner about something the trader is
               * not currently doing.
               */
              executionAccount={(() => {
                const c = connections.find((x) => x.id === selectedId)
                return c &&
                  (c.status === 'DEGRADED' || c.status === 'AUTH_FAILED')
                  ? { label: c.label, why: c.lastError }
                  : null
              })()}
              accountsLoaded={accountStore.loaded()}
              accountCount={connections.length}
              hasFeedAccount={
                diagnostics.socket.marketSource === 'SIMULATION' ||
                connections.some((c) => c.isMasterFeed)
              }
              canNominateFeed={connections.some(
                (c) => c.marketDataAccess === 'OWN_CREDENTIALS',
              )}
              chain={chain}
              onReconnect={() => tickSocket.reconnectNow()}
            />

            {/*
              ── the expiry strip ─────────────────────────────────────
              This row used to carry the strategy, the size and the fire
              buttons; they are on the top row now, which has the width
              for them since the index tabs and the expiry dropdown left
              it. What is here instead is the whole expiry series, which
              belongs over the CHAIN rather than over the workspace: it
              names the contracts in the ladder below it and nothing
              else on screen.

              UNDER THE NOTICE, NOT OVER IT. A banner is an interruption and
              appears and disappears with the feed's health; above the strip
              it shoved the chips down the moment it arrived, so the expiry
              the hand was reaching for moved under the cursor. Below it, the
              strip sits at a fixed distance from the ladder it names and the
              banner grows into the space above instead.
            */}
            <div className="tr-hdr">
              <ExpiryStrip
                expiries={expiries}
                value={expiry}
                onChange={(iso) => {
                  setExpiry(iso)
                }}
                onIntent={(iso) =>
                  onIntent(
                    () =>
                      void prefetchChain(underlying, iso).catch(() => undefined),
                  )
                }
              />
            </div>

            {/* ── the chain ─────────────────────────────────────────── */}
            <div
              className={`tr-tbl${chainModeClass(chainMode)}${chainColumnsClass(shed)}`}
              ref={bodyRef}
            >
              <ChainHeader
                mode={chainMode}
                expiry={expiry ? expiryLabel(expiry) : ''}
                onRefresh={greeks.refresh}
                showOi={showOi}
              />

              {/*
                Loading and failure are states of the ladder, shown INSIDE
                the table so the header, the spec row and the controls above
                stay put while a chain loads. A screen that reflows on every
                expiry click is one nobody can click twice quickly.
              */}
              {loading && visible.length === 0 && (
                <div className="tr-more text-[12px] text-muted-foreground">
                  Loading the chain…
                </div>
              )}
              {error && (
                <div
                  className="tr-more text-[12px]"
                  style={{ color: 'hsl(var(--destructive))' }}
                  data-testid="chain-error"
                >
                  {error}
                </div>
              )}
              {!loading && !error && visible.length === 0 && (
                <div className="tr-more text-[12px] text-muted-foreground">
                  No strikes for this expiry.
                </div>
              )}

              {hiddenAbove > 0 && (
                <div className="tr-more">
                  <button
                    type="button"
                    className="tr-morebtn"
                    onClick={showMoreAbove}
                  >
                    <ChevronUp size={14} />
                    Show {Math.min(hiddenAbove, LOAD_MORE_STRIKES)} lower
                    strikes
                  </button>
                </div>
              )}

              {visible.map((r) => {
                const ce = r.ce?.brokerToken ? Number(r.ce.brokerToken) : null
                const pe = r.pe?.brokerToken ? Number(r.pe.brokerToken) : null
                /*
                 * The spot marker sits BETWEEN the two strikes that
                 * straddle it — the first row at or above the spot gets
                 * the line drawn above it.
                 */
                /*
                 * NO SPOT MARKER. The line and its chip used to be drawn
                 * between the two strikes straddling the spot; both are gone
                 * from every mode. The ATM ROW IS THE MARKER now — one band
                 * across the ladder — and two indicators for one fact meant
                 * the eye had to reconcile them whenever the forward and the
                 * cash index disagreed, which is most of the time.
                 *
                 * The spot itself has not gone anywhere: it is in the header
                 * beside the instrument, which is where a number that is not
                 * about a particular strike belongs.
                 */
                return (
                  <div key={r.strike}>
                    <div
                      onMouseEnter={() => {
                        const token = ce ?? pe
                        if (token !== null) {
                          setHighlight({
                            token,
                            strike: r.strike,
                            optionSide: ce !== null ? 'CE' : 'PE',
                          })
                        }
                      }}
                    >
                      <ChainRow
                        mode={chainMode}
                        strike={r.strike}
                        ceToken={ce}
                        peToken={pe}
                        spot={spot}
                        isAtm={atm !== null && r.strike === atm}
                        maxCeOi={maxCeOi}
                        maxPeOi={maxPeOi}
                        showOi={showOi}
                        onTrade={fireLeg}
                        onCombo={fireCombo}
                        onLimit={limitTicket.open}
                        tradeDisabled={selected === null}
                        connectionId={selected?.id ?? null}
                        ceIv={
                          ce === null
                            ? null
                            : (greeks.byToken.get(ce)?.iv ?? null)
                        }
                        peIv={
                          pe === null
                            ? null
                            : (greeks.byToken.get(pe)?.iv ?? null)
                        }
                        ceIvWhy={
                          ce === null ? undefined : greeks.refusals.get(ce)
                        }
                        peIvWhy={
                          pe === null ? undefined : greeks.refusals.get(pe)
                        }
                        ceDelta={
                          ce === null
                            ? null
                            : (greeks.byToken.get(ce)?.greeks.delta ?? null)
                        }
                        peDelta={
                          pe === null
                            ? null
                            : (greeks.byToken.get(pe)?.greeks.delta ?? null)
                        }
                        ceGreeks={
                          ce === null ? null : greeks.byToken.get(ce)?.greeks
                        }
                        peGreeks={
                          pe === null ? null : greeks.byToken.get(pe)?.greeks
                        }
                        ceItmPct={
                          ce === null
                            ? null
                            : (greeks.byToken.get(ce)?.pItm ?? null)
                        }
                        peItmPct={
                          pe === null
                            ? null
                            : (greeks.byToken.get(pe)?.pItm ?? null)
                        }
                      />
                    </div>
                  </div>
                )
              })}

              {hiddenBelow > 0 && (
                <div className="tr-more">
                  <button
                    type="button"
                    className="tr-morebtn"
                    onClick={showMoreBelow}
                  >
                    <ChevronDown size={14} />
                    Show {Math.min(hiddenBelow, LOAD_MORE_STRIKES)} higher
                    strikes
                  </button>
                </div>
              )}
            </div>

            {/* ── legend ────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-4 border-t border-border px-4 py-2.5 text-[11.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: 'var(--tr-itm)',
                    border: '1px solid var(--tr-line3)',
                  }}
                />
                In the money
              </span>
              {chainMode === 'MARKET' && (
                <span className="inline-flex items-center gap-1.5">
                  {/* The legend has to be the bar's own colour, or it explains
                    something that is not on screen. */}
                  <span
                    style={{
                      width: 12,
                      height: 4,
                      borderRadius: 2,
                      background: 'var(--tr-bar-max)',
                    }}
                  />
                  Highest OI
                </span>
              )}
              <span>
                {chainMode === 'GREEKS'
                  ? 'Greeks per unit · IV in %'
                  : chainMode === 'COMBINED'
                    ? 'CE + PE per unit · IV in % · B and S send BOTH legs as one basket'
                    : 'OI in lakhs · IV in %'}
              </span>
              {/*
                WHY THE GREEKS ARE DASHES, when they are.

                After 15:30 on expiry day every price and open interest is
                still on screen — they are historical facts that survive
                settlement — while an expired option has no time value and
                therefore no volatility to solve for. Without this line the
                two look identical to a dead feed, which is how a settled
                chain gets reported as a bug.
              */}
              {greeks.computedAt !== null && greeks.years <= 0 && (
                <span className="text-warning">
                  This expiry has settled — no time value left to solve for
                </span>
              )}
              <span className="flex-1" />
              {/*
                9.25. Only on a SPLIT chain: at full width nothing is shed,
                so every box would be ticked and the control would have one
                state. `offeredAddons` is empty in Combined, which returns
                null of its own accord.
              */}
              {drawerOpen && (
                <ChainAddons
                  offered={offeredAddons}
                  value={addons}
                  onChange={pickAddons}
                />
              )}
              <span className="num">{visible.length} strikes shown</span>
            </div>
          </section>

          <StrategyPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            intent={pickerIntent}
            /*
             * ADJUSTMENTS — 9.69. The ticket hands over the contracts it
             * resolved and closes; the builder opens on them. Two surfaces
             * describing the same basket, one stale behind the other, is
             * the state where a trader places from the wrong one.
             */
            onAdjust={(seed) => {
              setBasketSeed(seed)
              setBasketOpen(true)
            }}
            /* The header's size control, whole: in ₹ mode the basket sizes
               itself from its own margin — only it knows what it costs. */
            sizing={
              sizeMode === 'LOTS'
                ? { mode: 'LOTS', lots: sendLots }
                : { mode: 'RUPEES', budget, armed }
            }
            greeks={greeks}
            isExpiryDay={isExpiryDay}
            /*
             * 9.43. The catalogue's expiry list, handed down so each LEG
             * can choose its own. The chain's own selection is untouched:
             * a calendar fired from the ticket does not change what the
             * table is rendering.
             */
            expiries={expiries}
            strategy={armedStrategy}
            /* A checklist edited inside the modal belongs to the strategy,
               so the armed copy has to follow it. */
            onStrategyChanged={setArmedStrategy}
            /*
             * The VISIBLE window, which is all the browser holds prices for.
             * A premium target outside it matches the best strike inside —
             * see the hook's header; it is why placing sends resolved tokens
             * rather than the criteria.
             */
            rows={previewRows}
            chainKinds={chainKinds}
            spot={spot}
            step={step}
            lotSize={lotSize ?? 0}
            underlying={underlying}
            expiry={expiry}
            connection={selected}
            // `openDrawer` refreshes the positions itself when that is the
            // tab, so this does not bump the nonce a second time.
            onPlaced={() => openDrawer('POSITIONS')}
          />

          {/*
            MOUNTED ONLY WHILE OPEN. The builder owns a whole second ladder
            — a catalogue, a greeks pass and its own subscriptions — and a
            component that returns null still runs every hook inside it.
          */}
          {basketOpen && (
          <BasketBuilder
            {...(basketSeed ? { seed: basketSeed } : {})}
            onClose={() => {
              setBasketOpen(false)
              /*
               * Dropped on close, so reopening from the strategy list does
               * not reopen the last adjusted basket. The basket itself is
               * saved by then — the builder writes it on unmount (9.67) —
               * and is in the drafts list where it belongs.
               */
              setBasketSeed(null)
            }}
            connection={selected}
            recent={recent}
            // `openDrawer` refreshes the positions itself for that tab.
            onPlaced={() => openDrawer('POSITIONS')}
          />
          )}

          <LimitOrderTicket
            ticket={limitTicket.ticket}
            onClose={limitTicket.close}
            onPlaced={() => {
              setPositionsRefresh((n) => n + 1)
              openDrawer('ORDERS')
            }}
          />
          <DrawerResizer
            active={drawerOpen}
            pct={drawerPct}
            onPct={setDrawerPct}
          />

          <BookDrawer
            payoffOpen={payoffOpen}
            onPayoffClose={() => setPayoffOpen(false)}
            open={drawerOpen}
            tab={drawerTab}
            onOpen={openDrawer}
            onClose={() => setDrawerOpen(false)}
            refreshToken={positionsRefresh}
            clock={clock}
            /*
             * Only while the drawer is OPEN. The split is remembered across
             * a collapse, so `drawerPct` alone would report a hidden chain
             * with nothing covering it — which is the state that rendered
             * an empty workspace.
             */
            chainHidden={chainFull}
            onShowChain={showChain}
            onFullScreen={showBookFullScreen}
            onPayoffOpen={() => setPayoffOpen(true)}
          />
        </div>
      </div>
    </div>
  )
}
